<template>
  <div v-if="data && data.length">
    <div class="search-input d-flex justify-content-end align-items-center mb-1" :class="{'hide-input': !!isReady}">
      <div class="input-group mr-3">
        <span class="input-group-prepend">
          <button class="btn btn-primary" type="button" @click="searchFiles">
            <font-awesome-icon icon="search"></font-awesome-icon>
          </button>
        </span>
        <input class="form-control" :placeholder="`Search in ${fileType}s`" v-model="fileQuery"
               @keyup.enter="searchFiles">
      </div>
    </div>
    <Table :show-download="false" :additional-columns="table.additionalColumns" :columns="table.columns"
           :data="table.values" @download="download" :is-selection-active="selectionActive" :show-select="false"
           :options="table.options" @select="handleSelectEvent">
    </Table>
  </div>
  <div v-else class="no-data d-flex flex-column align-items-center">
    <div class="no-data-input d-flex justify-content-end mb-1" :class="{'hide-input': !!isReady}">
      <div class="input-group">
        <span class="input-group-prepend">
          <button class="btn" type="button" @click="searchFiles">
            <font-awesome-icon icon="search"></font-awesome-icon>
          </button>
        </span>
        <input class="form-control" :placeholder="`Search in ${fileType}s`" v-model="fileQuery"
               @keyup.enter="searchFiles">
      </div>
    </div>
    <div v-if="!isReady" class="h-100 d-flex justify-content-center align-items-center">
      <i>No data</i>
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { mapActions, mapGetters } from 'vuex'
import studiesActions from '../../../store/modules/studies/actionTypes'
import studiesGetters from '../../../store/modules/studies/getterTypes'
import Table from '../../shared/Table'
import _ from 'lodash'

export default {
  name: 'FileTable',
  components: { Table },
  props: {
    data: {
      type: Array,
      required: false
    },
    fileType: String,
    isReady: Boolean
  },
  data () {
    return {
      table: {
        columns: ['category', 'product_type', 'client', 'c_mi_number', 'project_name', 'file_name'],
        additionalColumns: [],
        values: [],
        options: {
          headings: {
            'c_mi_number': 'CMI #',
            'selection': ''
          }
        }
      },
      fileQuery: '',
      selectionActive: false
    }
  },
  computed: {
    ...mapGetters({
      filterValues: studiesGetters.GET_SEARCH_FILTERS
    })
  },
  methods: {
    ...mapActions({
      getStudyFiles: studiesActions.FETCH_FILES
    }),
    formatValue (value) {
      if (!value || value === 'No Tag') {
        return '-'
      } else if (typeof value === 'string') {
        return value && value.charAt(0).toUpperCase() + value.slice(1)
      } else {
        return value
      }
    },
    download (file) {
      this.axios({
        url: '/study/' + file.id + '/study-files-path/' + file.file_name,
        method: 'GET',
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', file.file_name)
        document.body.appendChild(link)
        link.click()
      }).catch(res => {
        this.$emit('download-fail')
      })
    },
    handleSelectEvent ({ type, data = null }) {
      switch (type) {
        case 'edit':
          this.selectionActive = true
          break
        case 'cancel':
          this.selectionActive = false
          break
        case 'confirmEdit':
          this.$emit('multi-study-edit', data)
          this.selectionActive = false
          break
        default:
          this.selectionActive = false
      }
    },
    async searchFiles () {
      this.$emit('search')
      switch (this.fileType) {
        case 'RDF_report':
          await this.getStudyFiles({
            sq: this.parseDataForSearch(this.filterValues),
            fullSq: { file_type: '', search_phrase: this.fileQuery },
            fileType: 'RDF'
          })
          await this.getStudyFiles({
            sq: this.parseDataForSearch(this.filterValues),
            fullSq: { file_type: '', search_phrase: this.fileQuery },
            fileType: 'report'
          })
          break
        case 'protocol':
          await this.getStudyFiles({
            sq: this.parseDataForSearch(this.filterValues),
            fullSq: { file_type: '', search_phrase: this.fileQuery },
            fileType: 'protocol'
          })
          break
        case 'screener':
          await this.getStudyFiles({
            sq: this.parseDataForSearch(this.filterValues),
            fullSq: { file_type: '', search_phrase: this.fileQuery },
            fileType: 'screener'
          })
          break
        case 'questionnaire':
          await this.getStudyFiles({
            sq: this.parseDataForSearch(this.filterValues),
            fullSq: { file_type: '', search_phrase: this.fileQuery },
            fileType: 'questionnaire'
          })
          break
        default:
          console.warn('Unknown file type')
      }
      this.$emit('search-end')
    },
    parseDataForSearch (data) {
      let query = []
      const { startYear, endYear } = data.essentials
      if (startYear.value || endYear.value) {
        const year = {
          field: 'year',
          value: [startYear.value],
          value_secondary: [endYear.value]
        }
        query.push(year)
      }
      Object.keys(data).forEach(searchKey => {
        const searchCategory = data[searchKey]
        Object.keys(searchCategory).forEach(tagKey => {
          if (!['startYear', 'endYear'].includes(tagKey) && searchCategory[tagKey].value && searchCategory[tagKey].value.length) {
            query.push({
              field: searchCategory[tagKey].key,
              value: Array.isArray(searchCategory[tagKey].value) ? searchCategory[tagKey].value : [searchCategory[tagKey].value]
            })
          }
        })
      })
      query = query.filter(item => item.field !== 'rdf_type')
      return query
    }
  },
  watch: {
    data (newData) {
      if (newData && newData.length) {
        const dataClone = _.cloneDeep(newData)
        const dataMap = dataClone.map(el => {
          return { ...el.main, ...el.other, ...el.additional }
        })
        dataMap.forEach((el, index) => {
          Object.keys(el).forEach(key => {
            if (key !== 'id') {
              const newValue = this.formatValue(el[key])
              dataMap[index][key] = newValue
            }
          })
        })
        this.table.values = dataMap
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../../assets/scss/variables.scss";

.main-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 90px;
  width: 100%;
  cursor: pointer;
}

.additional-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
  width: 98%;
  cursor: pointer;
}

.search-input {
  position: relative;
  width: 100%;
  padding-right: 1rem;
  z-index: 4;
}

.search-input:hover .input-group {
  width: 230px;
}

.search-input .input-group {
  transition: width 0.35s ease-in;
  flex-wrap: nowrap;
  overflow: hidden;
  width: 230px;
}

.no-data-input {
  position: relative;
  width: 100%;
  padding-right: 1rem;
}

.no-data-input:hover .input-group {
  width: 230px;
}

.no-data-input .input-group {
  transition: width 0.35s ease-in;
  flex-wrap: nowrap;
  overflow: hidden;
  width: 230px;
}

.data {
  border-bottom: 1px solid #d1d4d7;
  transition: 0.1s background-color linear;
}

.data:hover {
  background-color: #f6f6f6;
}

.additional-data-hide {
  height: 0;
}

.additional-data-show {
  height: 50px;
  border-top: 1px dashed #d1d4d7
}

.data-show {
  height: 130px;
}

.data-hide {
  height: 90px;
}

.scroller {
  height: 100%;
}

.column-header {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  transition: 0.1s background-color linear;
}

.column-header:hover {
  background-color: #f6f6f6;
}

.column-body {
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-data {
  height: calc(55vh - 4rem);
}

.table-header {
  z-index: 3;
  position: sticky;
  top: 0;
  background-color: white;
  border-bottom: 1px solid #d1d4d7;
  padding: 0 12px;
}

.no-hover:hover {
  background-color: unset;
}

.hide-input {
  display: none !important;
}

.table-container {
  overflow: scroll;
  position: relative;
  max-height: calc(55vh - 4rem);
  min-height: 523px;
}

.btn-primary {
  background-color: $blue-500;
  &:hover {
    background-color: $blue-400;
  }
}
</style>
